import { Link, useLocation } from "@tanstack/react-router"
import { BookIcon, GearIcon, PersonIcon } from "@/components/icons"
import { cn } from "@/lib/utils"

// `sub` lists pages that live under a tab without sharing its path prefix.
const TABS = [
  { id: "stories", label: "Stories", to: "/stories", Icon: BookIcon, sub: [] },
  {
    id: "settings",
    label: "Settings",
    to: "/settings",
    Icon: GearIcon,
    sub: [],
  },
  {
    id: "account",
    label: "Account",
    to: "/account",
    Icon: PersonIcon,
    sub: ["/contact"],
  },
] as const

export type TabId = (typeof TABS)[number]["id"]

const isUnder = (pathname: string, base: string) =>
  pathname === base || pathname.startsWith(`${base}/`)

/** The tab a path belongs to: the tab's route, its sub-pages, or anything
 *  below them. */
export const activeTabFor = (pathname: string): TabId | null =>
  TABS.find(
    ({ to, sub }) =>
      isUnder(pathname, to) ||
      sub.some((base: string) => isUnder(pathname, base))
  )?.id ?? null

/** Phone layout bottom navigation. Hidden in the desktop layout. */
export const TabBar = () => {
  const { pathname } = useLocation()
  const active = activeTabFor(pathname)

  return (
    <nav
      aria-label="Tabs"
      className="fixed inset-x-0 bottom-0 z-40 border-t-[0.5px] border-(--surface-hairline) bg-background/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-md desktop:hidden"
    >
      <div className="mx-auto flex h-(--tab-bar-row-height) max-w-(--shell-max-width)">
        {TABS.map(({ id, label, to, Icon }) => (
          <Link
            key={id}
            to={to}
            aria-current={id === active ? "page" : undefined}
            className={cn(
              "flex min-h-11 flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-medium",
              id === active ? "text-primary" : "text-muted-foreground"
            )}
          >
            <Icon className="size-[26px]" />
            {label}
          </Link>
        ))}
      </div>
    </nav>
  )
}
